import React from 'react';
import './Container.css';

/**
 * Container component with ArbiWin design system integration
 * Responsive container for page layouts with consistent max-widths and padding
 * Based on header_and_footer.html mockup container behavior
 *
 * @param {Object} props - Container properties
 * @param {React.ReactNode} props.children - Container content
 * @param {string} props.size - Container size: 'small', 'medium', 'large', 'xlarge', 'full'
 * @param {boolean} props.fluid - Make container full width
 * @param {boolean} props.centered - Center container content
 * @param {string} props.padding - Padding variant: 'none', 'small', 'medium', 'large'
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @param {React.ElementType} props.as - HTML element or React component to render as
 * @returns {JSX.Element} Container component
 */
const Container = function Container({
  children,
  size = 'large',
  fluid = false,
  centered = true,
  padding = 'medium',
  className = '',
  testId,
  as: Component = 'div',
  ...rest
}) {

  const containerClasses = [
    'arbiwin-container',
    'arbiwin-container--size-' + size,
    'arbiwin-container--padding-' + padding,
    fluid ? 'arbiwin-container--fluid' : '',
    centered ? 'arbiwin-container--centered' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <Component
      className={containerClasses}
      data-testid={testId || 'arbiwin-container'}
      {...rest}
    >
      {children}
    </Component>
  );
};

/**
 * PageContainer component - Main page wrapper matching mockup structure
 * Based on header_and_footer.html layout
 *
 * @param {Object} props - PageContainer properties
 * @param {React.ReactNode} props.children - Page content
 * @param {boolean} props.fullHeight - Make container full viewport height
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} PageContainer component
 */
const PageContainer = function PageContainer({
  children,
  fullHeight = true,
  className = ''
}) {
  return (
    <div
      className={[
        'arbiwin-page-container',
        fullHeight ? 'arbiwin-page-container--full-height' : '',
        className
      ].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
};

/**
 * HeaderContainer component - Container for header content
 * Matches the header structure from header_and_footer.html
 *
 * @param {Object} props - HeaderContainer properties
 * @param {React.ReactNode} props.children - Header content
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} HeaderContainer component
 */
const HeaderContainer = function HeaderContainer({
  children,
  className = ''
}) {
  return (
    <Container
      size="full"
      padding="medium"
      className={'arbiwin-header-container ' + className}
      as="div"
    >
      {children}
    </Container>
  );
};

/**
 * MainContainer component - Container for main content area
 *
 * @param {Object} props - MainContainer properties
 * @param {React.ReactNode} props.children - Main content
 * @param {string} props.size - Container size
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} MainContainer component
 */
const MainContainer = function MainContainer({
  children,
  size = 'large',
  className = ''
}) {
  return (
    <Container
      size={size}
      padding="large"
      className={'arbiwin-main-container ' + className}
      as="main"
    >
      {children}
    </Container>
  );
};

/**
 * FooterContainer component - Container for footer content
 * Based on footer structure from header_and_footer.html
 *
 * @param {Object} props - FooterContainer properties
 * @param {React.ReactNode} props.children - Footer content
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FooterContainer component
 */
const FooterContainer = function FooterContainer({
  children,
  className = ''
}) {
  return (
    <Container
      size="full"
      padding="large"
      className={'arbiwin-footer-container ' + className}
      as="div"
    >
      {children}
    </Container>
  );
};

/**
 * ContentContainer component - Flexible content container
 *
 * @param {Object} props - ContentContainer properties
 * @param {React.ReactNode} props.children - Content
 * @param {string} props.variant - Container variant: 'default', 'narrow', 'wide', 'article'
 * @param {boolean} props.withSidebar - Include sidebar space
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ContentContainer component
 */
const ContentContainer = function ContentContainer({
  children,
  variant = 'default',
  withSidebar = false,
  className = ''
}) {

  const variantSizeMap = {
    narrow: 'small',
    default: 'medium',
    wide: 'large',
    article: 'medium'
  };

  const size = variantSizeMap[variant] || 'medium';

  return (
    <Container
      size={size}
      className={[
        'arbiwin-content-container',
        'arbiwin-content-container--' + variant,
        withSidebar ? 'arbiwin-content-container--with-sidebar' : '',
        className
      ].filter(Boolean).join(' ')}
    >
      {children}
    </Container>
  );
};

Container.displayName = 'Container';
PageContainer.displayName = 'PageContainer';
HeaderContainer.displayName = 'HeaderContainer';
MainContainer.displayName = 'MainContainer';
FooterContainer.displayName = 'FooterContainer';
ContentContainer.displayName = 'ContentContainer';

export default Container;
export {
  PageContainer,
  HeaderContainer,
  MainContainer,
  FooterContainer,
  ContentContainer
};
